import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import type { FlexProps, TextAlignProps } from 'styled-system';
import { color, flex, fontSize, lineHeight, space, textAlign } from 'styled-system';

import { ButtonLink } from 'common/components/Button';
import { Flex } from 'common/components/Flex';
import type { CheckboxFieldProps } from 'common/components/Form/CheckboxField';
import CheckboxField from 'common/components/Form/CheckboxField';
import type { LinkStylesProps } from 'common/components/Link';
import { linkStyles } from 'common/components/Link';
import { BodyRegularBR2 } from 'common/components/Typography';
import { CheckboxValue, FieldChangeTrigger } from 'common/enums/form';
import { useKeyboardSelect } from 'common/hooks/keyboard';
import { useLocalization } from 'common/providers/LocaleProvider';
import type { FieldName } from '../enums';

type CommonCheckboxProps = Pick<
  CheckboxFieldProps<FieldName>,
  'name' | 'value' | 'onChange' | 'disabled' | 'eventName'
>;

export interface ConsentCheckboxProps extends CommonCheckboxProps {
  labelText: string;
  helpText?: string;
  helpTextUrl?: string;
  onClick?: () => void;
}

const HelpText = styled(BodyRegularBR2)<FlexProps & TextAlignProps & LinkStylesProps>`
  ${flex};
  ${textAlign};
  ${space};

  a,
  button {
    ${props => linkStyles({ styled: true, textDecoration: 'underline', ...props })};
  }
`;
const helpTextStyles: ComponentProps<typeof HelpText> = {
  textAlign: 'left',
  flex: '1 1 0%',
  ml: 'sp_32',
};

const StyledButtonLink = styled(ButtonLink)`
  ${fontSize};
  ${lineHeight};
  ${color};
  ${textAlign};
`;
const buttonLinkStyles = {
  fontSize: 'inherit',
  lineHeight: 'inherit',
  color: 'inherit',
  textAlign: 'left',
};

export const ConsentCheckbox = ({
  labelText,
  helpText,
  helpTextUrl,
  onClick,
  ...checkboxProps
}: ConsentCheckboxProps) => {
  const { r, t, prefix } = useLocalization();
  const handleToggle = useCallback(() => {
    checkboxProps.onChange?.({
      fieldName: checkboxProps.name,
      value: checkboxProps.value === CheckboxValue.True ? CheckboxValue.False : CheckboxValue.True,
      trigger: FieldChangeTrigger.Change,
    });
  }, [checkboxProps]);

  const onCheckboxKeyDown = useKeyboardSelect(handleToggle);
  const onHelpTextKeyDown = useKeyboardSelect(onClick);

  const checkboxLabel = (
    <div
      dangerouslySetInnerHTML={{
        __html: t(labelText),
      }}
    />
  );

  const renderHelpContent = () => {
    if (helpTextUrl) {
      return (
        <span
          dangerouslySetInnerHTML={{
            __html: r.t(helpText, { href: prefix(t(helpTextUrl)) }),
          }}
        />
      );
    }

    return (
      <StyledButtonLink
        kind="bare"
        as="button"
        onClick={onClick}
        onKeyDown={onHelpTextKeyDown}
        {...buttonLinkStyles}
      >
        {t(helpText)}
      </StyledButtonLink>
    );
  };

  return (
    <>
      <Flex flexDirection="column" onKeyDown={onCheckboxKeyDown}>
        <CheckboxField label={checkboxLabel} {...checkboxProps} />
      </Flex>

      {helpText && <HelpText {...helpTextStyles}>{renderHelpContent()}</HelpText>}
    </>
  );
};
